import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { TableCell, TableRow } from 'src/components/ui/table';

const MIN_SKELETON_ROWS = 8;
/** Approximate rendered height of one skeleton row (py-3 + two-line cell). */
const SKELETON_ROW_HEIGHT_PX = 60;
/** Extend skeleton slightly below the viewport so the list feels full while loading. */
const SKELETON_EXTRA_PX = 48;

function useViewportSkeletonRowCount() {
  const anchorRef = useRef<HTMLTableRowElement>(null);
  const [rowCount, setRowCount] = useState(MIN_SKELETON_ROWS);

  const recalculate = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const top = anchor.getBoundingClientRect().top;
    const rowsNeeded = Math.ceil(
      (window.innerHeight - top + SKELETON_EXTRA_PX) / SKELETON_ROW_HEIGHT_PX,
    );
    setRowCount((prev) => {
      const next = Math.max(MIN_SKELETON_ROWS, rowsNeeded);
      return prev === next ? prev : next;
    });
  }, []);

  useLayoutEffect(() => {
    recalculate();
    window.addEventListener('resize', recalculate);
    return () => window.removeEventListener('resize', recalculate);
  }, [recalculate]);

  return { anchorRef, rowCount };
}

/** Pulsating placeholder rows matching the invoice list table layout. */
const InvoiceTableSkeleton: React.FC = () => {
  const { anchorRef, rowCount } = useViewportSkeletonRowCount();

  return (
    <>
      {Array.from({ length: rowCount }).map((_, index) => (
        <TableRow
          key={`invoice-skeleton-${index}`}
          ref={index === 0 ? anchorRef : undefined}
          className="border-b border-gray-100"
        >
          <TableCell className="w-[3%] min-w-[30px] border-r border-gray-201 py-3">
            <Skeleton className="mx-auto h-4 w-4 rounded" />
          </TableCell>
          <TableCell className="w-[35%] min-w-[250px] max-w-[250px] border-r border-gray-201 py-3">
            <div className="space-y-2">
              <Skeleton className={`h-4 ${index % 3 === 0 ? 'w-[72%]' : index % 3 === 1 ? 'w-[58%]' : 'w-[65%]'}`} />
              <Skeleton className="h-3 w-[40%]" />
            </div>
          </TableCell>
          <TableCell className="w-[15%] min-w-[70px] max-w-[70px] border-r border-gray-201 py-3">
            <Skeleton className="h-4 w-[70%]" />
          </TableCell>
          <TableCell className="w-[15%] min-w-[100px] max-w-[100px] border-r border-gray-201 py-3">
            <Skeleton className="ml-auto h-4 w-[60%]" />
          </TableCell>
          <TableCell className="w-[15%] min-w-[100px] max-w-[100px] border-r border-gray-201 py-3">
            <Skeleton className="ml-auto h-4 w-[55%]" />
          </TableCell>
          <TableCell className="w-[15%] min-w-[100px] max-w-[100px] border-r border-gray-201 py-3">
            <Skeleton className="mx-auto h-6 w-[72%] rounded-full" />
          </TableCell>
          <TableCell className="w-[3%] min-w-[30px] max-w-[30px] py-3">
            <Skeleton className="mx-auto h-4 w-4 rounded" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

export default InvoiceTableSkeleton;
